import type {
    CreateGreyTemperature,
    CreateThemeTokens,
    CreateThemeKey,
    CreateSettings,
    CreatePalette,
    CreatePreset
} from "./settings";
import {
    CHART_SERIES_COUNT,
    type ChartPairDelta,
    CHART_SERIES_PATH,
    getChartPairDelta
} from "./chart";
import {
    getRelativeChroma,
    getHueDifference,
    getContrastRatio,
    type ColorVector,
    toGammaChannel,
    compositeOver,
    normalizeHue,
    getMaxChroma,
    getLuminance,
    rgbToOklch,
    oklchToRgb,
    formatRgba,
    parseColor,
    formatHex,
    getDeltaE
} from "./color";
import familiesJSON from "./families.json";
import themeJSON from "../../../src/variants/themes/default.json";

export type CreateTokenFamily = "fixed" | "brand" | "tertiary" | "neutral";

export type CreateGreyTone = Exclude<CreateGreyTemperature, "auto">;

export type CreateTokenFamilyEntry = {
    family: CreateTokenFamily;
    isRgba: boolean;
    alpha?: number;
};

export type CreateBrandControls = {
    saturation: number;
    lightness: number;
    hue: number;
};

type ShippedPalette = {
    themes: Record<CreateThemeKey, CreateThemeTokens>;
    name: string;
};

type TokenTree = {
    [key: string]: TokenTree | string;
};

type ChartOrder = {
    displacement: number;
    order: Array<number>;
    isPassing: boolean;
    score: number;
};

type BrandContext = {
    referencePrimary: Record<CreateThemeKey, ColorVector>;
    anchors: Record<CreateThemeKey, ColorVector | null>;
    chromaScale: number;
    hue: number;
};

export const CREATE_FAMILIES = familiesJSON as Record<CreateThemeKey, Record<string, CreateTokenFamilyEntry>>;

const SHIPPED_PALETTES = themeJSON.palettes as unknown as Array<ShippedPalette>;

const REFERENCE_PALETTE_NAME = "nibgat";

const PRIMARY_PATH = "system.default.primary";

const PRIMARY_CONTAINER_PATH = "content.container.primary";

const ON_PRIMARY_PATH = "content.text.onPrimary";

const TEXT_PATH_PREFIX = "content.text.";

const ANCHOR_SURFACE_CONTRAST = 3;

const ANCHOR_TEXT_CONTRAST = 4.5;

const CHART_AREA_PATH = "chart.categoricalArea";

const THEME_KEYS: Array<CreateThemeKey> = [
    "light",
    "dark"
];

const GROUNDS: Record<CreateThemeKey, ColorVector> = {
    dark: [
        20 / 255,
        20 / 255,
        20 / 255
    ],
    light: [
        1,
        1,
        1
    ]
};

const GREY_HUES: Record<Exclude<CreateGreyTone, "pure">, number> = {
    cool: 260,
    warm: 60
};

const DAYLIGHT_EDGES = {
    cool: 261,
    warm: 77
};

const NEUTRAL_MAX_CHROMA = 0.006;

const NEUTRAL_PEAK_CHROMA = 0.0055;

const NEUTRAL_DARK_RAMP = 0.4;

const NEUTRAL_LIGHT_RAMP = 0.02;

const ACHROMATIC_CHROMA = 0.004;

const LIGHTNESS_STEPS = 24;

const ALPHA_STEPS = 24;

const getShippedPalette = (name: string) => {
    return SHIPPED_PALETTES.find((palette) => palette.name === name) ?? SHIPPED_PALETTES[0]!;
};

const cloneTokens = (tokens: CreateThemeTokens): TokenTree => {
    return JSON.parse(JSON.stringify(tokens)) as TokenTree;
};

const readPath = (tree: TokenTree, path: string) => {
    let current: TokenTree | string | undefined = tree;

    for(const key of path.split(".")) {
        if(current === undefined || typeof current === "string") {
            return undefined;
        }

        current = current[key];
    }

    return typeof current === "string" ? current : undefined;
};

const writePath = (tree: TokenTree, path: string, value: string) => {
    const keys = path.split(".");

    const lastKey = keys.pop();

    let current = tree;

    for(const key of keys) {
        const next = current[key];

        if(next === undefined || typeof next === "string") {
            return;
        }

        current = next;
    }

    if(lastKey !== undefined) {
        current[lastKey] = value;
    }
};

const toVector = (value: string | undefined) => {
    return parseColor(value ?? "")?.rgb ?? GROUNDS.light;
};

const getTargetLuminance = ({
    groundLuminance,
    isDarker,
    contrast
}: {
    groundLuminance: number;
    isDarker: boolean;
    contrast: number;
}) => {
    const luminance = isDarker ? (groundLuminance + 0.05) / contrast - 0.05 : contrast * (groundLuminance + 0.05) - 0.05;

    return Math.min(Math.max(luminance, 0), 1);
};

const solveLightness = ({
    luminance,
    getColor
}: {
    getColor: (lightness: number) => ColorVector;
    luminance: number;
}) => {
    let low = 0;

    let high = 1;

    for(let step = 0; step < LIGHTNESS_STEPS; step++) {
        const middle = (low + high) / 2;

        if(getLuminance(getColor(middle)) < luminance) {
            low = middle;
        } else {
            high = middle;
        }
    }

    return getColor((low + high) / 2);
};

const getCornerCandidates = (color: ColorVector) => {
    const scaled = color.map((channel) => Math.min(Math.max(channel, 0), 1) * 255);

    return Array.from({
        length: 8
    }, (_, corner): ColorVector => [
        (corner & 1 ? Math.ceil(scaled[0] ?? 0) : Math.floor(scaled[0] ?? 0)) / 255,
        (corner & 2 ? Math.ceil(scaled[1] ?? 0) : Math.floor(scaled[1] ?? 0)) / 255,
        (corner & 4 ? Math.ceil(scaled[2] ?? 0) : Math.floor(scaled[2] ?? 0)) / 255
    ]);
};

const getGreyCandidates = (color: ColorVector) => {
    const grey = toGammaChannel(getLuminance(color)) * 255;

    return [
        Math.floor(grey),
        Math.ceil(grey)
    ].map((channel): ColorVector => [
        channel / 255,
        channel / 255,
        channel / 255
    ]);
};

const isNeutralCandidate = (color: ColorVector) => {
    const oklch = rgbToOklch(color);

    const isMagentaSide = normalizeHue(oklch[2] - DAYLIGHT_EDGES.cool) <= normalizeHue(DAYLIGHT_EDGES.warm - DAYLIGHT_EDGES.cool);

    return isMagentaSide && oklch[1] <= NEUTRAL_MAX_CHROMA;
};

const snapToChannels = ({
    candidates,
    contrast,
    ground,
    color
}: {
    candidates: Array<ColorVector>;
    ground: ColorVector;
    color: ColorVector;
    contrast: number;
}): ColorVector => {
    let best: ColorVector = candidates[0] ?? color;

    let bestCost = Number.POSITIVE_INFINITY;

    for(const candidate of candidates) {
        const cost = Math.abs(getContrastRatio(candidate, ground) / contrast - 1) * 100 + getDeltaE(candidate, color);

        if(cost < bestCost) {
            bestCost = cost;
            best = candidate;
        }
    }

    return best;
};

const solveAlpha = ({
    contrast,
    ground,
    ink
}: {
    ground: ColorVector;
    contrast: number;
    ink: ColorVector;
}) => {
    let low = 0;

    let high = 1;

    for(let step = 0; step < ALPHA_STEPS; step++) {
        const middle = (low + high) / 2;

        const layer = compositeOver({
            background: ground,
            foreground: ink,
            alpha: middle
        });

        if(getContrastRatio(layer, ground) < contrast) {
            low = middle;
        } else {
            high = middle;
        }
    }

    return (low + high) / 2;
};

const getNeutralChroma = (lightness: number) => {
    return Math.min(NEUTRAL_MAX_CHROMA, NEUTRAL_PEAK_CHROMA * Math.min(1, lightness / NEUTRAL_DARK_RAMP) * Math.min(1, Math.max(0, 1 - lightness) / NEUTRAL_LIGHT_RAMP));
};

export const resolveGreyTemperature = ({
    temperature,
    brand
}: {
    temperature: CreateGreyTemperature;
    brand: string;
}): CreateGreyTone => {
    if(temperature !== "auto") {
        return temperature;
    }

    const brandColor = rgbToOklch(toVector(brand));

    if(brandColor[1] < ACHROMATIC_CHROMA) {
        return "pure";
    }

    if(brandColor[2] <= 110 || brandColor[2] >= 330) {
        return "warm";
    }

    return brandColor[2] >= 220 ? "cool" : "pure";
};

const solveNeutralInk = ({
    temperature,
    reference,
    ground
}: {
    temperature: CreateGreyTone;
    reference: ColorVector;
    ground: ColorVector;
}) => {
    const contrast = getContrastRatio(reference, ground);

    const luminance = getTargetLuminance({
        isDarker: getLuminance(reference) < getLuminance(ground),
        groundLuminance: getLuminance(ground),
        contrast
    });

    if(temperature === "pure") {
        const grey = toGammaChannel(luminance);

        return snapToChannels({
            candidates: getGreyCandidates([
                grey,
                grey,
                grey
            ]),
            color: [
                grey,
                grey,
                grey
            ],
            contrast,
            ground
        });
    }

    const color = solveLightness({
        getColor: (lightness) => oklchToRgb([
            lightness,
            getNeutralChroma(lightness),
            GREY_HUES[temperature]
        ]),
        luminance
    });

    return snapToChannels({
        candidates: [
            ...getCornerCandidates(color).filter(isNeutralCandidate),
            ...getGreyCandidates(color)
        ],
        contrast,
        ground,
        color
    });
};

const solveBrandInk = ({
    reference,
    themeKey,
    ground,
    brand
}: {
    themeKey: CreateThemeKey;
    reference: ColorVector;
    ground: ColorVector;
    brand: BrandContext;
}) => {
    const referenceColor = rgbToOklch(reference);

    const contrast = getContrastRatio(reference, ground);

    const hue = normalizeHue(brand.hue + getHueDifference(rgbToOklch(brand.referencePrimary[themeKey])[2], referenceColor[2]));

    const chroma = referenceColor[1] * brand.chromaScale;

    const color = solveLightness({
        luminance: getTargetLuminance({
            isDarker: getLuminance(reference) < getLuminance(ground),
            groundLuminance: getLuminance(ground),
            contrast
        }),
        getColor: (lightness) => oklchToRgb([
            lightness,
            Math.min(getMaxChroma(lightness, hue), chroma),
            hue
        ])
    });

    return snapToChannels({
        candidates: getCornerCandidates(color),
        contrast,
        ground,
        color
    });
};

const isSameColor = (first: ColorVector, second: ColorVector) => formatHex(first) === formatHex(second);

const getOnPrimary = (themeKey: CreateThemeKey) => {
    return toVector(readPath(getShippedPalette(REFERENCE_PALETTE_NAME).themes[themeKey] as TokenTree, ON_PRIMARY_PATH));
};

const isAnchorable = (color: ColorVector, themeKey: CreateThemeKey) => {
    return getContrastRatio(color, getOnPrimary(themeKey)) >= ANCHOR_TEXT_CONTRAST && getContrastRatio(color, GROUNDS[themeKey]) >= ANCHOR_SURFACE_CONTRAST;
};

const getMirroredAnchor = (brand: ColorVector) => {
    const color = rgbToOklch(brand);

    const hue = normalizeHue(color[2]);

    const mirrored = solveLightness({
        getColor: (lightness) => oklchToRgb([
            lightness,
            Math.min(getMaxChroma(lightness, hue), color[1]),
            hue
        ]),
        luminance: Math.min(getTargetLuminance({
            contrast: getContrastRatio(brand, GROUNDS.light),
            groundLuminance: getLuminance(GROUNDS.dark),
            isDarker: false
        }), getLuminance(getOnPrimary("light")))
    });

    return isAnchorable(mirrored, "dark") ? mirrored : null;
};

const getAnchors = (brand: ColorVector): Record<CreateThemeKey, ColorVector | null> => {
    const light = isAnchorable(brand, "light") ? brand : null;

    if(isAnchorable(brand, "dark")) {
        return {
            dark: brand,
            light
        };
    }

    return {
        dark: light ? getMirroredAnchor(brand) : null,
        light
    };
};

const getBrandContext = (brand: string): BrandContext => {
    const reference = getShippedPalette(REFERENCE_PALETTE_NAME);

    const referencePrimary: Record<CreateThemeKey, ColorVector> = {
        light: toVector(readPath(reference.themes.light as TokenTree, PRIMARY_PATH)),
        dark: toVector(readPath(reference.themes.dark as TokenTree, PRIMARY_PATH))
    };

    const brandColor = rgbToOklch(toVector(brand));

    const referenceChroma = rgbToOklch(referencePrimary.light)[1];

    return {
        chromaScale: referenceChroma > 0 ? brandColor[1] / referenceChroma : 0,
        anchors: getAnchors(toVector(brand)),
        hue: brandColor[2],
        referencePrimary
    };
};

const generateTheme = ({
    temperature,
    themeKey,
    brand
}: {
    temperature: CreateGreyTone;
    themeKey: CreateThemeKey;
    brand: BrandContext;
}) => {
    const reference = getShippedPalette(REFERENCE_PALETTE_NAME).themes[themeKey] as TokenTree;

    const tokens = cloneTokens(reference);

    const ground = GROUNDS[themeKey];

    const anchor = brand.anchors[themeKey];

    const anchorSources = [
        toVector(readPath(reference, PRIMARY_PATH)),
        toVector(readPath(reference, PRIMARY_CONTAINER_PATH))
    ];

    Object.entries(CREATE_FAMILIES[themeKey]).forEach(([
        path,
        entry
    ]) => {
        const parsed = parseColor(readPath(reference, path) ?? "");

        if(!parsed || entry.family === "fixed") {
            return;
        }

        const isAnchored = anchor !== null && entry.family === "brand" && anchorSources.some((source) => isSameColor(source, parsed.rgb)) && (!path.startsWith(TEXT_PATH_PREFIX) || getContrastRatio(anchor, ground) >= ANCHOR_TEXT_CONTRAST);

        const solvedInk = entry.family === "neutral" ? solveNeutralInk({
            reference: parsed.rgb,
            temperature,
            ground
        }) : solveBrandInk({
            reference: parsed.rgb,
            themeKey,
            ground,
            brand
        });

        const ink = isAnchored && anchor ? anchor : solvedInk;

        if(!parsed.isRgba) {
            writePath(tokens, path, formatHex(ink));

            return;
        }

        const referenceLayer = compositeOver({
            foreground: parsed.rgb,
            alpha: parsed.alpha,
            background: ground
        });

        writePath(tokens, path, formatRgba({
            alpha: solveAlpha({
                contrast: getContrastRatio(referenceLayer, ground),
                ground,
                ink
            }),
            rgb: ink
        }));
    });

    return tokens;
};

const getPermutations = (items: Array<number>): Array<Array<number>> => {
    if(items.length <= 1) {
        return [
            items
        ];
    }

    return items.flatMap((item, index) => getPermutations([
        ...items.slice(0, index),
        ...items.slice(index + 1)
    ]).map((rest) => [
        item,
        ...rest
    ]));
};

const CHART_ORDERS = getPermutations(Array.from({
    length: CHART_SERIES_COUNT - 1
}, (_, index) => index + 1));

const isBetterOrder = (candidate: ChartOrder, best: ChartOrder | null) => {
    if(!best) {
        return true;
    }

    if(candidate.isPassing !== best.isPassing) {
        return candidate.isPassing;
    }

    if(candidate.isPassing && candidate.displacement !== best.displacement) {
        return candidate.displacement < best.displacement;
    }

    return candidate.score > best.score;
};

const getChartOrder = (deltas: Array<Array<Array<ChartPairDelta>>>) => {
    let best: ChartOrder | null = null;

    for(const order of CHART_ORDERS) {
        const sequence = [
            0,
            ...order
        ];

        let isPassing = true;

        let score = Number.POSITIVE_INFINITY;

        for(const matrix of deltas) {
            for(let position = 1; position < sequence.length; position++) {
                const delta = matrix[sequence[position - 1] ?? 0]?.[sequence[position] ?? 0];

                if(delta) {
                    isPassing = isPassing && delta.isPassing;
                    score = Math.min(score, delta.score);
                }
            }
        }

        const candidate: ChartOrder = {
            displacement: order.reduce((sum, source, position) => sum + Math.abs(source - position - 1), 0),
            isPassing,
            order,
            score
        };

        if(isBetterOrder(candidate, best)) {
            best = candidate;
        }
    }

    return best;
};

const assignChartSeries = (themes: Record<CreateThemeKey, TokenTree>) => {
    const reference = getShippedPalette(REFERENCE_PALETTE_NAME);

    const deltas = THEME_KEYS.map((themeKey) => {
        const colors = Array.from({
            length: CHART_SERIES_COUNT
        }, (_, index) => toVector(readPath(index === 0 ? themes[themeKey] : reference.themes[themeKey] as TokenTree, `${CHART_SERIES_PATH}.${index + 1}`)));

        return colors.map((first) => colors.map((second) => getChartPairDelta(first, second)));
    });

    const best = getChartOrder(deltas);

    if(!best) {
        return;
    }

    for(const themeKey of THEME_KEYS) {
        const source = reference.themes[themeKey] as TokenTree;

        best.order.forEach((sourceIndex, position) => {
            [
                CHART_SERIES_PATH,
                CHART_AREA_PATH
            ].forEach((path) => {
                const value = readPath(source, `${path}.${sourceIndex + 1}`);

                if(value !== undefined) {
                    writePath(themes[themeKey], `${path}.${position + 2}`, value);
                }
            });
        });
    }
};

export const getPresetBrand = (preset: CreatePreset) => {
    const palette = getShippedPalette(preset === "custom" ? REFERENCE_PALETTE_NAME : preset);

    return readPath(palette.themes.light as TokenTree, PRIMARY_PATH) ?? "";
};

export const normalizeBrand = (brand: string) => {
    const parsed = parseColor(brand);

    return parsed && !parsed.isRgba ? formatHex(parsed.rgb) : null;
};

export const getBrandControls = (brand: string): CreateBrandControls => {
    const color = rgbToOklch(toVector(brand));

    return {
        saturation: getRelativeChroma(color),
        lightness: color[0],
        hue: color[2]
    };
};

export const getBrandFromControls = ({
    saturation,
    lightness,
    hue
}: CreateBrandControls) => {
    const chroma = getMaxChroma(lightness, normalizeHue(hue)) * Math.min(Math.max(saturation, 0), 1);

    return formatHex(oklchToRgb([
        lightness,
        chroma,
        normalizeHue(hue)
    ]));
};

export const generatePalette = (settings: CreateSettings): CreatePalette => {
    if(settings.preset !== "custom") {
        const palette = getShippedPalette(settings.preset);

        return {
            brandAdjusted: {
                light: readPath(palette.themes.light as TokenTree, PRIMARY_PATH) ?? "",
                dark: readPath(palette.themes.dark as TokenTree, PRIMARY_PATH) ?? ""
            },
            themes: {
                light: cloneTokens(palette.themes.light),
                dark: cloneTokens(palette.themes.dark)
            }
        };
    }

    const brandHex = normalizeBrand(settings.brand) ?? getPresetBrand("nibgat");

    const brand = getBrandContext(brandHex);

    const temperature = resolveGreyTemperature({
        temperature: settings.greyTemperature,
        brand: brandHex
    });

    const light = generateTheme({
        themeKey: "light",
        temperature,
        brand
    });

    const dark = generateTheme({
        themeKey: "dark",
        temperature,
        brand
    });

    assignChartSeries({
        light,
        dark
    });

    return {
        brandAdjusted: {
            light: readPath(light, PRIMARY_PATH) ?? brandHex,
            dark: readPath(dark, PRIMARY_PATH) ?? brandHex
        },
        themes: {
            light,
            dark
        }
    };
};
